import { Injectable } from '@angular/core';
import { Router, ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { JwtHelperService }          from '@auth0/angular-jwt';
import { ProductsService }    from "../_services/index";
import { UtilService }         from '../shared/util.service';
import {NotificationsService} from '../notifications/notifications.service';
import { CustomerService } from 'app/shared/customer.service';
import { AlertService } from 'app/core-lib/services/alerts/alert.service';
import { Store } from '@ngxs/store';
import { CustomersState } from 'app/states/customers/customers.state';
import { FilterDateService } from '../shared/filters/date/filter-date-service';
import { AuthenticationService } from '../_services/authentication.service';
import * as moment from 'moment';

@Injectable()
export class AuthGuard  {

  jwtHelper: JwtHelperService = new JwtHelperService();

  constructor(
    private router: Router,
    private productsService: ProductsService,
    private utilService: UtilService,
    private notificationsService: NotificationsService,
    private customerService: CustomerService,
    private alertService: AlertService,
    private store: Store,
    private filterDateService: FilterDateService,
    private authenticationService: AuthenticationService,
  ) {}

  async canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot) {
    let currentHotel: any = this.utilService.getCurrentHotel();
    let customerIdFromUrl = route.queryParams['customer_id'] || route.queryParams['customer'] || route.params['customer'];
    if (customerIdFromUrl) {
      customerIdFromUrl = String(customerIdFromUrl).trim();
    }

    if (this.isLogged() && currentHotel) {
      const currentUser = this.utilService.getCurrentUser();
      
      // Si hay customer_id en la URL y es diferente al actual
      if (customerIdFromUrl && String(currentHotel?.id) !== String(customerIdFromUrl)) {
        const targetCustomerId = parseInt(customerIdFromUrl);
        const hasAccess = this.checkCustomerAccess(currentUser, targetCustomerId);
        
        if (hasAccess) {
          try {
            const newCustomer: any = await this.customerService.findCustomer(targetCustomerId).toPromise();
            if (newCustomer) {
              await this.utilService.customerChanged(newCustomer);
              currentHotel = newCustomer;
            }
          } catch (error) {
            console.error('Error loading customer from URL:', error);
          }
        } else {
          // No tiene acceso al customer de la URL, limpiar params y quedarse en el actual
          console.error('User does not have access to customer:', targetCustomerId);
          this.alertService.handleAlert(
            'concierge.conversations.no_access_to_conversation',
            5000,
            'bottom',
            'end',
            'snackbar-panel-error'
          );
          // Navegar a la misma ruta pero sin los query params
          const cleanUrl = this.clearUrl(state.url);
          this.router.navigate([cleanUrl]);
          return false;
        }
      }

      // Manejar filtro de fecha si vienen los parámetros customer_id, guest y date
      if ((route.queryParams['customer_id'] || route.queryParams['customer']) && route.queryParams['guest'] && route.queryParams['date']) {
        const date = route.queryParams['date'];
        const fromDate = moment(date).format('YYYY-MM-DD');
        const toDate = moment(date).format('YYYY-MM-DD');
        this.filterDateService.setCustomDate({ fromDate, toDate });
      }
      
      if (this.productsService.navItems.length === 0 || customerIdFromUrl) {
        const isChain = await this.customerService.getIsChain(currentHotel, currentUser);
        this.productsService.dataNavbar = null;
        await this.productsService.loadProducts(
          this.utilService.getCurrentUser(),
          this.utilService.getCurrentHotel(),
          this.utilService.getFidelityConfigurations(),
          isChain,
        );
      }

      if (this.productsService.hasNotProduct(state.url) || +currentHotel?.id === 12) {
        return true;
      }

      return this.checkViewItem(state);
    }

    this.logout();
    const queryParams = Object.assign({}, route.queryParams);
    const path = Object.keys(queryParams).length > 0 ? state.url.substring(0, state.url.indexOf('?')) : state.url;
    queryParams.returnUrl = path.startsWith('/') ? path : '/' + path;
    this.router.navigate(['/login'], { queryParams: queryParams });
    return false;
  }

  private checkCustomerAccess(currentUser: any, targetCustomerId: number): boolean {
    // SuperAdmin siempre tiene acceso
    const isSuperAdmin = currentUser?.isSuperAdmin?.() || currentUser?.superAdmin;
    if (isSuperAdmin) {
      return true;
    }

    // Verificar en los customers del usuario
    if (currentUser?.customers && currentUser.customers.length > 0) {
      const hasAccessInUser = currentUser.customers.some((c: any) => 
        String(c.id) === String(targetCustomerId)
      );
      if (hasAccessInUser) {
        return true;
      }
    }

    // Verificar en relativeCurrentChain del store
    const relativeChain = this.store.selectSnapshot(CustomersState.relativeCurrentChain);
    if (relativeChain && relativeChain.length > 0) {
      const hasAccessInChain = relativeChain.some((c: any) => 
        String(c.id) === String(targetCustomerId)
      );
      if (hasAccessInChain) {
        return true;
      }
    }

    // Si no encontró acceso en ninguna fuente, denegar
    return false;
  }

  isLogged(): boolean {
    const currentJWT: any = localStorage.getItem('currentJWT');
    const jwt = currentJWT ? JSON.parse(currentJWT) : null;
    let isLogged = false;
    if(jwt) return true;
    else{
      this.logout();
      isLogged = false;
    }
    return isLogged;
  }

  checkViewItem(state: RouterStateSnapshot){
    const path = this.clearUrl(state.url);
    const itemProduct = this.productsService.getProductItemByLink(path)
      || this.productsService.getProductItemByLink(path.startsWith('/') ? path.slice(1) : '/' + path);
    this.checkCustomerStatus();

    if (itemProduct === undefined) {
      const url = this.productsService.findLeafItem(
        this.productsService.dataNavbar.filter((item) => item.slug !== 'corporative'),
      );
      this.alertService.handleAlert(
        'commons.not_permissions',
        5000,
        'bottom',
        'end',
        'snackbar-panel-error-user-config',
      );
      this.router.navigate([url]);
      return false;
    }

    return true;
  }

  logout() {
    this.authenticationService.logout();
  }

  checkCustomerStatus() {
    this.notificationsService.checkModalAlert('guard');
  }

  clearUrl(link){
    return link.indexOf("?") > 0 ? link.substring(0,link.indexOf("?")) : link;
  }

}
